import { deepAnalysisApi } from '../service';
import { GenericProcess } from '../../process/types';
import {
    SitemapImportProcess,
    AddSitemapImportProcessParams,
    FetchActiveSitemapImportProcessesParams,
    DomainSetupProcess,
    AddUrlAnalysisProcessParams,
    FetchActiveOnPageSeoAnalysisProcessesParams,
    AddGenericAnalysisProcessResponse,
    AddGenericAnalysisProcessParams,
} from './types';


const processApi = deepAnalysisApi.injectEndpoints({
    endpoints: (builder) => ({
        fetchActiveSitemapImportProcesses: builder.query<SitemapImportProcess[], FetchActiveSitemapImportProcessesParams>({
            query: (args) => {
                const { w_guid } = args;

                return {
                    method: 'GET',
                    url: `/import-sitemap?w_guid=${w_guid}`,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.w_guid}_sitemap_import` }],
        }),

        addSitemapImportProcess: builder.mutation<SitemapImportProcess, AddSitemapImportProcessParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/import-sitemap',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.w_guid}_sitemap_import` }],
        }),

        fetchActiveOnPageSeoAnalysisProcesses: builder.query<GenericProcess[], FetchActiveOnPageSeoAnalysisProcessesParams>({
            query: (args) => {
                const { process_type, w_guid } = args;

                return {
                    method: 'GET',
                    url: `/on-page-seo/active?process_type=${process_type}&w_guid=${w_guid}`,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.w_guid}_${args.process_type}` }],
        }),

        addUrlAnalysisProcess: builder.mutation<AddGenericAnalysisProcessResponse, AddUrlAnalysisProcessParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/on-page-seo/url',
                };
            },
        }),

        addUrlAnalysisProcessBatch: builder.mutation<AddGenericAnalysisProcessResponse, AddGenericAnalysisProcessParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/on-page-seo/batch',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.w_guid}_on_page_seo_batch_url_analysis` }],
        }),

        addDomainAnalysisProcess: builder.mutation<AddGenericAnalysisProcessResponse, AddGenericAnalysisProcessParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/on-page-seo/domain',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.w_guid}_on_page_seo_domain_analysis` }],
        }),

        createDomainSetupProcess: builder.mutation<{ guid: string }, { domain: string }>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: '/website-index/domain-setup',
                };
            },

            invalidatesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_setup` }],
        }),

        fetchDomainSetupProcess: builder.query<DomainSetupProcess, { domain: string, processGuid: string | null }>({
            query: (args) => {
                const { domain, processGuid } = args;
                const endpoint = processGuid ? `/website-index/domain-setup?domain=${encodeURIComponent(domain)}&process_guid=${processGuid}` : `/website-index/domain-setup?domain=${encodeURIComponent(domain)}`;
                return {
                    method: 'GET',
                    url: endpoint,
                };
            },

            providesTags: (result, error, args) => [{ type: 'ActiveProcesses', id: `${args.domain}_domain_setup` }],
        }),
    }),
});

export const {
    useAddDomainAnalysisProcessMutation,
    useAddSitemapImportProcessMutation,
    useAddUrlAnalysisProcessBatchMutation,
    useAddUrlAnalysisProcessMutation,
    useFetchActiveOnPageSeoAnalysisProcessesQuery,
    useFetchActiveSitemapImportProcessesQuery,
    useFetchDomainSetupProcessQuery,
    useCreateDomainSetupProcessMutation,
} = processApi;
